import { OpportunityStatus, OpportunityType } from "@/graphql"

export enum IconFamily {
	Listing = "listing",
	Offmarket = "offmarket",
	ListingPotential = "listing-potential",
	OpportunityTarget = "opportunity-target",
}

export enum IconBadge {
	New = "new",
	Liked = "liked",
	Discarded = "discarded",
	Offer = "offer",
	Syncing = "syncing",
}

const ONE_MONTH_MS = 2592000000

const FAMILY_COLORS: Record<IconFamily, string> = {
	[IconFamily.Listing]: "#1DBF73",
	[IconFamily.Offmarket]: "#14181F",
	[IconFamily.ListingPotential]: "#9EDFBC",
	[IconFamily.OpportunityTarget]: "#C84141",
}

export function resolveIconFamily(type: string | undefined | null): { family: IconFamily; color: string } | null {
	const family = ((): IconFamily | null => {
		switch (type) {
			case OpportunityType.ListingLanderz:
			case "landerz":
				return IconFamily.Listing
			case OpportunityType.OffMarketOfficial:
				return IconFamily.Offmarket
			case OpportunityType.ListingPotential:
				return IconFamily.ListingPotential
			case OpportunityType.OpportunityTarget:
				return IconFamily.OpportunityTarget
			default:
				return null
		}
	})()

	return family ? { family, color: FAMILY_COLORS[family] } : null
}

export function resolveBadges(props: {
	status?: string | null
	salesProcessStatus?: string | null
	launchDateMs?: number | null
	isPlaceholder?: boolean | null
}): IconBadge[] {
	const badges: IconBadge[] = []

	if (props.isPlaceholder) {
		badges.push(IconBadge.Syncing)
	} else if (props.status === OpportunityStatus.Like) {
		badges.push(IconBadge.Liked)
	} else if (props.status === OpportunityStatus.Dislike) {
		badges.push(IconBadge.Discarded)
	} else if (props.launchDateMs != null && props.launchDateMs > Date.now() - ONE_MONTH_MS) {
		badges.push(IconBadge.New)
	}
	if (props.salesProcessStatus === "accepted_psa") {
		badges.push(IconBadge.Offer)
	}

	return badges
}

export function iconName(family: IconFamily, badges: IconBadge[]): string {
	if (badges.length === 0) return family
	return `${family}--${[...badges].sort().join("-")}`
}

const CANVAS_SIZE = 56
const MAIN_RADIUS = 20
const MAIN_CENTER = CANVAS_SIZE / 2
const BADGE_RADIUS = MAIN_RADIUS * 0.5

interface SourceFrame {
	cx: number
	cy: number
	r: number
}

// maps source coordinates onto a target circle
function sourceTransform(target: SourceFrame, source: SourceFrame): string {
	const scale = target.r / source.r
	return `translate(${target.cx} ${target.cy}) scale(${scale}) translate(${-source.cx} ${-source.cy})`
}

const TOP_RIGHT: SourceFrame = {
	cx: MAIN_CENTER + MAIN_RADIUS * 0.9495,
	cy: MAIN_CENTER - MAIN_RADIUS * 0.6,
	r: BADGE_RADIUS,
}
const BOTTOM_RIGHT: SourceFrame = {
	cx: MAIN_CENTER + MAIN_RADIUS * 0.9483,
	cy: MAIN_CENTER + MAIN_RADIUS * 0.6323,
	r: BADGE_RADIUS,
}

function badgeSlot(badge: IconBadge): SourceFrame {
	return badge === IconBadge.Offer ? BOTTOM_RIGHT : TOP_RIGHT
}

const BADGE_COLORS: Record<IconBadge, string> = {
	[IconBadge.New]: "#2F6FE0",
	[IconBadge.Liked]: "#FBBC04",
	[IconBadge.Discarded]: "#54667D",
	[IconBadge.Offer]: "#F6655A",
	[IconBadge.Syncing]: "#8A94A6",
}

// traced from original design files
const FLAG_SOURCE: SourceFrame = { cx: 377.088, cy: 377.083, r: 243.28 }
const FLAG_PATH =
	"M507.571 369.428C506.14 368.921 478.79 359.284 444.069 374.9C408.376 390.917 406.999 397.751 367.662 416.304C346.413 426.342 329.863 425.568 329.863 425.568L295.655 365.237L331.996 519.321C332.17 520.092 332.036 520.899 331.624 521.575C331.212 522.251 330.552 522.744 329.782 522.952C329.005 523.151 328.181 523.046 327.481 522.658C326.782 522.27 326.261 521.629 326.029 520.87L255.831 280.746C255.468 279.509 255.36 278.212 255.512 276.932C255.665 275.653 256.076 274.416 256.72 273.296C257.364 272.176 258.23 271.195 259.264 270.412C260.299 269.628 261.481 269.057 262.743 268.734C264.002 268.407 265.314 268.334 266.603 268.518C267.891 268.701 269.13 269.138 270.244 269.802C271.359 270.467 272.328 271.345 273.094 272.386C273.86 273.426 274.407 274.608 274.704 275.861L277.97 289.796C291.746 292.241 305.928 291.091 319.117 286.459C365.232 270.442 385.562 244.201 428.869 233.416C443.118 229.969 458.047 230.402 472.067 234.671C472.067 234.671 449.118 246.577 419.311 310.672C419.257 310.672 463.184 327.597 507.571 369.428Z"

const STAR_SOURCE: SourceFrame = { cx: 49.1343, cy: 15.0955, r: 12.5796 }
const STAR_PATH =
	"M55.6145 13.2354L51.6654 12.6615L49.9001 9.08261C49.8519 8.98463 49.7726 8.9053 49.6746 8.85709C49.4289 8.73577 49.1302 8.83687 49.0074 9.08261L47.242 12.6615L43.293 13.2354C43.1841 13.2509 43.0846 13.3023 43.0084 13.38C42.9163 13.4747 42.8655 13.6021 42.8672 13.7343C42.869 13.8664 42.9231 13.9924 43.0177 14.0846L45.8749 16.8702L45.1999 20.8037C45.184 20.8952 45.1942 20.9893 45.2291 21.0754C45.264 21.1614 45.3224 21.2359 45.3975 21.2905C45.4726 21.3451 45.5616 21.3775 45.6542 21.3841C45.7468 21.3907 45.8394 21.3712 45.9216 21.3279L49.4537 19.4708L52.9859 21.3279C53.0824 21.3792 53.1944 21.3963 53.3017 21.3776C53.5723 21.331 53.7076 21.0743 53.7076 20.8037L53.0326 16.8702L55.8898 14.0846C55.9675 14.0084 56.0189 13.9089 56.0344 13.8C56.0764 13.5278 55.8867 13.2758 55.6145 13.2354Z"

const DOLLAR_SOURCE: SourceFrame = { cx: 49.103, cy: 43.66175, r: 12.5796 }
const DOLLAR_PATH =
	"M49.1098 38.3896C50.5049 38.4273 52.022 39.0261 52.8837 39.8878L49.1098 38.3896ZM49.1098 38.3896C47.4493 38.3443 45.9649 39.0953 45.9649 41.1458C45.9649 44.9196 52.8837 43.0327 52.8837 46.8066C52.8837 48.9589 51.042 49.8835 49.1098 49.8144M49.1098 38.3896V49.8144M49.1098 38.3896V36.1139M49.1098 49.8144C47.6544 49.7628 46.1461 49.1464 45.3359 48.0645L49.1098 49.8144ZM49.1098 49.8144V52.4674"

const HEART_SOURCE: SourceFrame = { cx: 235, cy: 236, r: 434 }
const HEART_PATH =
	"M433.601 67.001c-24.7-24.7-57.4-38.2-92.3-38.2s-67.7 13.6-92.4 38.3l-12.9 12.9-13.1-13.1c-24.7-24.7-57.6-38.4-92.5-38.4-34.8 0-67.6 13.6-92.2 38.2-24.7 24.7-38.3 57.5-38.2 92.4 0 34.9 13.7 67.6 38.4 92.3l187.8 187.8c2.6 2.6 6.1 4 9.5 4 3.4 0 6.9-1.3 9.5-3.9l188.2-187.5c24.7-24.7 38.3-57.5 38.3-92.4 0-34.9-13.5-67.7-38.2-92.4z"

const EYE_OFF_SOURCE: SourceFrame = { cx: 12, cy: 12, r: 18 }
const EYE_OFF_PATH =
	"M9.88 9.88a3 3 0 1 0 4.24 4.24M10.73 5.08A10.43 10.43 0 0 1 12 5c7 0 10 7 10 7a13.16 13.16 0 0 1-1.67 2.68M6.61 6.61A13.526 13.526 0 0 0 2 12s3 7 10 7a9.74 9.74 0 0 0 5.39-1.61M2 2l20 20"

const SYNCING_SOURCE: SourceFrame = { cx: 12, cy: 12, r: 18 }
const SYNCING_PATH =
	"M21 12a9 9 0 0 0-9-9 9.75 9.75 0 0 0-6.74 2.74L3 8M3 3v5h5M3 12a9 9 0 0 0 9 9 9.75 9.75 0 0 0 6.74-2.74L21 16M16 16h5v5"

// badges are drawn inverted: white disc, coloured ring and glyph
const INVERTED_BADGES = new Set<IconBadge>([
	IconBadge.New,
	IconBadge.Liked,
	IconBadge.Discarded,
	IconBadge.Offer,
	IconBadge.Syncing,
])
const INVERTED_BADGE_FILL = "#ffffff"
const INVERTED_BADGE_RING_WIDTH = 1.5

function badgeGlyphMarkup(badge: IconBadge, target: SourceFrame): string {
	const glyphColor = INVERTED_BADGES.has(badge) ? BADGE_COLORS[badge] : "white"
	switch (badge) {
		case IconBadge.New:
			return `<path transform="${sourceTransform(target, STAR_SOURCE)}" d="${STAR_PATH}" fill="${glyphColor}" />`
		case IconBadge.Liked:
			return `<path transform="${sourceTransform(target, HEART_SOURCE)}" d="${HEART_PATH}" fill="${glyphColor}" />`
		case IconBadge.Discarded:
			return (
				`<path transform="${sourceTransform(target, EYE_OFF_SOURCE)}" d="${EYE_OFF_PATH}" ` +
				`fill="none" stroke="${glyphColor}" stroke-width="3" stroke-linecap="round" stroke-linejoin="round" />`
			)
		case IconBadge.Offer:
			return (
				`<path transform="${sourceTransform(target, DOLLAR_SOURCE)}" d="${DOLLAR_PATH}" ` +
				`fill="none" stroke="${glyphColor}" stroke-width="1.88694" stroke-linecap="round" stroke-linejoin="round" />`
			)
		case IconBadge.Syncing:
			return (
				`<path transform="${sourceTransform(target, SYNCING_SOURCE)}" d="${SYNCING_PATH}" ` +
				`fill="none" stroke="${glyphColor}" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />`
			)
	}
}

function badgeCircleMarkup(badge: IconBadge, slot: SourceFrame, filterId?: string): string {
	const inverted = INVERTED_BADGES.has(badge)
	const fill = inverted ? INVERTED_BADGE_FILL : BADGE_COLORS[badge]
	const ring = inverted ? ` stroke="${BADGE_COLORS[badge]}" stroke-width="${INVERTED_BADGE_RING_WIDTH}"` : ""
	const filter = filterId ? ` filter="url(#${filterId})"` : ""
	return `<circle cx="${slot.cx}" cy="${slot.cy}" r="${slot.r}" fill="${fill}"${ring}${filter} />`
}

const BADGE_SHADOW_MARGIN = 3 // keeps the badge drop shadow from being clipped
const BADGE_SHADOW_FILTER_ID = "opportunity-icon-badge-shadow"

// symmetric keeps Mapbox's icon-anchor from shifting as badges change; the
// default stays tight from the top-left corner, for left-aligned layouts.
function contentBounds(
	badges: IconBadge[],
	{ symmetric }: { symmetric: boolean } = { symmetric: false }
): { minX: number; minY: number; width: number; height: number } {
	if (symmetric) {
		let offsetX = MAIN_RADIUS
		let offsetY = MAIN_RADIUS
		for (const badge of badges) {
			const slot = badgeSlot(badge)
			offsetX = Math.max(offsetX, Math.abs(slot.cx - MAIN_CENTER) + slot.r + BADGE_SHADOW_MARGIN)
			offsetY = Math.max(offsetY, Math.abs(slot.cy - MAIN_CENTER) + slot.r + BADGE_SHADOW_MARGIN)
		}
		return {
			minX: MAIN_CENTER - offsetX,
			minY: MAIN_CENTER - offsetY,
			width: offsetX * 2,
			height: offsetY * 2,
		}
	}

	let minX = MAIN_CENTER - MAIN_RADIUS
	let minY = MAIN_CENTER - MAIN_RADIUS
	let maxX = MAIN_CENTER + MAIN_RADIUS
	let maxY = MAIN_CENTER + MAIN_RADIUS

	for (const badge of badges) {
		const slot = badgeSlot(badge)
		minX = Math.min(minX, slot.cx - slot.r - BADGE_SHADOW_MARGIN)
		minY = Math.min(minY, slot.cy - slot.r - BADGE_SHADOW_MARGIN)
		maxX = Math.max(maxX, slot.cx + slot.r + BADGE_SHADOW_MARGIN)
		maxY = Math.max(maxY, slot.cy + slot.r + BADGE_SHADOW_MARGIN)
	}

	return { minX, minY, width: maxX - minX, height: maxY - minY }
}

// scales so the circle renders at circleDiameter; a badge grows the box, not the circle
export function iconDisplaySize(badges: IconBadge[], circleDiameter: number): { width: number; height: number } {
	const bounds = contentBounds(badges)
	const scale = circleDiameter / (MAIN_RADIUS * 2)
	return { width: bounds.width * scale, height: bounds.height * scale }
}

export function buildOpportunityIconSvg(
	color: string,
	badges: IconBadge[],
	options: { symmetric: boolean } = { symmetric: false }
): string {
	const badgeMarkup = badges
		.map(badge => {
			const slot = badgeSlot(badge)
			return badgeCircleMarkup(badge, slot, BADGE_SHADOW_FILTER_ID) + badgeGlyphMarkup(badge, slot)
		})
		.join("")

	const shadowDefs =
		badges.length === 0
			? ""
			: `<defs><filter id="${BADGE_SHADOW_FILTER_ID}" x="-50%" y="-50%" width="200%" height="200%">` +
				`<feDropShadow dx="0" dy="0.6" stdDeviation="0.8" flood-color="#1A1A1A" flood-opacity="0.35" />` +
				`</filter></defs>`

	const flagTransform = sourceTransform({ cx: MAIN_CENTER, cy: MAIN_CENTER, r: MAIN_RADIUS }, FLAG_SOURCE)
	const bounds = contentBounds(badges, options)

	return (
		`<svg xmlns="http://www.w3.org/2000/svg" width="${bounds.width}" height="${bounds.height}" ` +
		`viewBox="${bounds.minX} ${bounds.minY} ${bounds.width} ${bounds.height}">` +
		shadowDefs +
		`<circle cx="${MAIN_CENTER}" cy="${MAIN_CENTER}" r="${MAIN_RADIUS}" fill="${color}" />` +
		`<path transform="${flagTransform}" d="${FLAG_PATH}" fill="white" />` +
		`${badgeMarkup}</svg>`
	)
}

export function buildOpportunityIconDataUri(color: string, badges: IconBadge[] = []): string {
	return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(buildOpportunityIconSvg(color, badges))}`
}

// rounded-square take on the listing marker, for the collapsed side nav brand mark
export function buildLanderzMarkDataUri(): string {
	const color = FAMILY_COLORS[IconFamily.Listing]
	const size = MAIN_RADIUS * 2
	const origin = MAIN_CENTER - MAIN_RADIUS
	const margin = MAIN_RADIUS * 0.5
	const viewSize = size + margin * 2
	const flagTransform = sourceTransform({ cx: MAIN_CENTER, cy: MAIN_CENTER, r: MAIN_RADIUS }, FLAG_SOURCE)
	const svg =
		`<svg xmlns="http://www.w3.org/2000/svg" width="${viewSize}" height="${viewSize}" ` +
		`viewBox="${origin - margin} ${origin - margin} ${viewSize} ${viewSize}">` +
		`<defs><filter id="landerz-mark-glow" x="-50%" y="-50%" width="200%" height="200%">` +
		`<feGaussianBlur stdDeviation="2.5" />` +
		`</filter></defs>` +
		`<rect x="${origin}" y="${origin + 2}" width="${size}" height="${size}" rx="12" fill="#1A1A1A" ` +
		`opacity="0.25" filter="url(#landerz-mark-glow)" />` +
		`<rect x="${origin}" y="${origin}" width="${size}" height="${size}" rx="12" fill="${color}" ` +
		`opacity="0.5" filter="url(#landerz-mark-glow)" />` +
		`<rect x="${origin}" y="${origin}" width="${size}" height="${size}" rx="12" fill="${color}" />` +
		`<path transform="${flagTransform}" d="${FLAG_PATH}" fill="white" />` +
		`</svg>`
	return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`
}

// a standalone badge circle, no flag -- for legends
export function buildBadgeIconDataUri(badge: IconBadge): string {
	const target: SourceFrame = { cx: BADGE_RADIUS, cy: BADGE_RADIUS, r: BADGE_RADIUS }
	const circleSlot: SourceFrame = { cx: BADGE_RADIUS, cy: BADGE_RADIUS, r: BADGE_RADIUS - INVERTED_BADGE_RING_WIDTH }
	const svg =
		`<svg xmlns="http://www.w3.org/2000/svg" width="${BADGE_RADIUS * 2}" height="${BADGE_RADIUS * 2}" ` +
		`viewBox="0 0 ${BADGE_RADIUS * 2} ${BADGE_RADIUS * 2}">` +
		badgeCircleMarkup(badge, circleSlot) +
		badgeGlyphMarkup(badge, target) +
		`</svg>`
	return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`
}

// every combination, to pre-register all Mapbox images upfront
export function enumerateOpportunityIcons(): { name: string; color: string; badges: IconBadge[] }[] {
	const statuses: (string | null)[] = [null, OpportunityStatus.Like, OpportunityStatus.Dislike]
	const seen = new Set<string>()
	const icons: { name: string; color: string; badges: IconBadge[] }[] = []

	for (const family of Object.values(IconFamily)) {
		const color = FAMILY_COLORS[family]
		for (const status of statuses) {
			for (const isNew of [false, true]) {
				for (const hasOffer of [false, true]) {
					for (const isPlaceholder of [false, true]) {
						const badges = resolveBadges({
							status,
							launchDateMs: isNew ? Date.now() : null,
							salesProcessStatus: hasOffer ? "accepted_psa" : null,
							isPlaceholder,
						})
						const name = iconName(family, badges)
						if (seen.has(name)) continue
						seen.add(name)
						icons.push({ name, color, badges })
					}
				}
			}
		}
	}

	return icons
}
